import classNames from 'classnames';
import { getSearch, push } from 'connected-react-router';
import { Pathname, Search } from 'history';
import { isEmpty } from 'lodash';
import * as queryString from 'query-string';
import { Dispatch, FC, ReactNode, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { usePrevious } from 'react-use';

import { ProductModel } from '../../../../models/products.model';
import { updateQueryString } from '../../../../providers/location.provider';
import { productListData, getProductList, dataList, bookingYear } from '../../../../store/location';
import { AppState } from '../../../../store/store';
import { loadersByName, popupByName } from '../../../../store/ui';
import { useMedia } from '../../../context/InterfaceContext';
import AymoProduct from '../../shared/aymoProduct';
import ControlViewsLocation from '../../shared/controlViewsLocation';
import Loading from '../../shared/Loading';
import MapContainer from '../../shared/mapContainer';
import NotSearchResult from '../../shared/notSearchResult';
import Product from '../../shared/product';
import './index.scss';

const ProductList: FC<{ setShowList: Dispatch<boolean>; showList: boolean }> = ({ showList, setShowList }) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const products = useSelector(productListData);
    const productList: ProductModel[] = Array.isArray(products) ? products : Object.values(products);
    const listAllData = useSelector(dataList);
    const bookingyear = useSelector(bookingYear);
    const locationLoading = useSelector(loadersByName('location'));
    const { opened: isOpenedFilter } = useSelector(popupByName('location_filter'));
    const searchState = useSelector((state: AppState) => getSearch(state));
    const [showAll, setShowAll] = useState<boolean>(false);
    const [offset, setOffset] = useState(0);
    const prevOffset = usePrevious(offset);
    const prevSearchState = usePrevious(searchState);
    const { isMobileOrTablet, isFullMobile } = useMedia();
    const searchParams: queryString.ParsedQuery = queryString.parse(searchState, { arrayFormat: 'comma' });
    const isIndividualProduct = !!searchParams.individualproduct;
    const numberAymo: number = isIndividualProduct ? 0 : 1;

    const getSearchString = (urn: string) => updateQueryString(searchState, { urn });

    useEffect(() => {
        if (listAllData.data.count === 0) {
            setShowList(true);
            setOffset(0);
        }

        if (listAllData.data && listAllData.data.eod) {
            setShowAll(false);
        }
    }, [listAllData, setShowList]);

    useEffect(() => {
        if (showAll) {
            dispatch(getProductList({ payload: { limit: 9000, offset: 0, bookingyear: bookingyear }, reset: true }));
            setOffset(0);
        }
    }, [dispatch, showAll, bookingyear]);

    useEffect(() => {
        if (prevSearchState !== searchState) {
            setOffset(0);
        }
    }, [prevSearchState, searchState]);

    const list = () => {
        if (!productList || (isEmpty(productList) && !!productList)) {
            return null;
        }

        const aymo = <AymoProduct horizonMode={!showList} key={'item-aymo-product'} />;
        const extraEmptyElements = 4 - (productList.length % 4);
        const emptyElements: ReactNode[] = [];

        for (let i = 0; i < extraEmptyElements; i++) {
            emptyElements.push(<div key={`empty-${i}`} className="location__empty-element"></div>);
        }

        return (
            <InfiniteScroll
                scrollableTarget="location__scrollable-list"
                className={classNames('location__product-list', {
                    ['location__product-list--horizon-mode']: !showList,
                })}
                dataLength={productList.length + numberAymo}
                next={() => {
                    if (!(isOpenedFilter || showAll)) {
                        const offsetValue = (prevOffset || 0) + 24;
                        dispatch(getProductList({ payload: { offset: offsetValue, bookingyear: bookingyear } }));
                        setOffset(offsetValue);
                    }
                }}
                hasMore={!listAllData.data.eod}
                loader={
                    <div className="w-100 loader-wrapper">
                        <Loading relative />
                    </div>
                }
            >
                {productList.map((item, idx) => {
                    const length = productList.length;

                    return (
                        <>
                            {idx === 3 && length > 3 && !isIndividualProduct && aymo}
                            <Product key={`product-${item.producturn}`} product={item} horizontalMode={!showList} />
                            {idx + 1 === length && length <= 3 && !isIndividualProduct && aymo}
                        </>
                    );
                })}
                {extraEmptyElements > 0 && emptyElements}
            </InfiniteScroll>
        );
    };

    return listAllData.data.count ? (
        <>
            <div className={classNames('location__left-side', { ['location__left-side--active']: showList })}>
                <ControlViewsLocation
                    counter={{
                        showingNow: productList.length + numberAymo,
                        total: (listAllData.data.groupcount ?? productList.length) + numberAymo,
                    }}
                    controls={{ toggle: showList, setToggle: toggle => setShowList(toggle) }}
                />

                {showList
                    ? list()
                    : !isMobileOrTablet && !isFullMobile && <div id="location__scrollable-list">{list()}</div>}
            </div>

            <div className={classNames('location__right-side', { ['location__right-side--show']: !showList })}>
                <div className="location__map-container">
                    <span
                        className={classNames('location__show-all-btn', { ['location__show-all-btn--active']: showAll })}
                        onClick={() => {
                            if (!showAll) {
                                setShowAll(true);
                            }
                        }}
                        title="Alle Produkte anzeigen"
                    >
                        {locationLoading ? (
                            <Loading style={{ margin: 0 }} relative />
                        ) : (
                            intl.formatMessage({ id: 'map.showAll', defaultMessage: 'Alle anzeigen' })
                        )}
                    </span>

                    <MapContainer
                        locations={productList}
                        center={listAllData.centerCordsMap as google.maps.LatLngLiteral}
                        useCustomMarker
                        push={(location: { pathname?: Pathname; search?: Search }) => dispatch(push(location))}
                        getSearch={getSearchString}
                    />
                </div>
            </div>
        </>
    ) : !locationLoading && !isOpenedFilter ? (
        <NotSearchResult />
    ) : null;
};

export default ProductList;
